import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 2.1 visual preview at /preview/lesson/2.1
 *
 * "Not all models are the same" — Week 2 opener. The tooling pre-check that
 * sits before the four content checks from 1.4.
 *
 * Promotes "Tool use" in as the new vocab term with the full 4-block treatment.
 * 3 model-pick drills where students choose which configuration to trust.
 * Explains Constare's own routing (the Option C cost design) from the student
 * side: drilling gets a fast model, audit/judgment work gets a smarter one.
 */

export default function LessonPreviewPage2_1() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 2 · Working with AI · Lesson 2.1
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Not all models are the same
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Judgment · Character</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">3 model-pick drills</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Same prompt. Two AIs. Two very different answers."
      accent="amber"
    >
      <p>
        Two students sit down to research the same question for an assignment:
        <span className="italic">
          {' '}
          What does the latest research say about teens and social media?
        </span>
      </p>
      <p>
        They both type the same prompt. They both get an answer in under ten
        seconds. They both feel confident. Here is what they got.
      </p>

      <ModelComparison />

      <p className="mt-4">
        Student A copies their answer into the assignment. Student B reads
        their answer, notices the "no real-time data" disclaimer, and goes to
        verify. Same question. Same effort. Wildly different outcomes.
      </p>
      <p>
        The difference is not who tried harder. The difference is that
        Student A's tool was the wrong tool for the question, and Student A
        did not know to check. Lesson 2.1 fixes that.
      </p>
      <p className="text-text">
        The audit framework from 1.4 catches problems <Vocab>after</Vocab>{' '}
        the AI answers. The tooling pre-check catches the problem{' '}
        <Vocab>before</Vocab> you even hit Send. Three questions, every time.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time · Week 1 review"
      eyebrow="Three questions across the whole first week. No looking back."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> Name the four
          audit checks, in order, from Lesson 1.4.
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> ChatGPT is a
          chatbot powered by an LLM. Which two bigger boxes sit above LLM in
          the AI landscape from 1.5?
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> Name the three
          working rules for your data from 1.5.
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        Hold your answers. Week 2 assumes you have Week 1 in your back
        pocket. If any of those three are blank, that is the lesson to revisit
        first.
      </p>
    </Section>
  );
}

// ---------- Teaching ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="Teaching"
      eyebrow="Three questions to ask before you trust any AI output."
    >
      <p>
        The audit framework from 1.4 is what you run on an answer{' '}
        <Vocab>after</Vocab> the AI gives it to you. The tooling pre-check is
        what you run on the AI <Vocab>before</Vocab> you even ask the
        question. It takes about ten seconds. It changes how much you should
        trust whatever comes back.
      </p>
      <p>
        Three questions, in order. The order matters because the first
        question often determines whether the other two are even relevant.
      </p>

      <Subhead n="1" title="Which model am I actually talking to?" />
      <p>
        Not every AI is the same AI. The same chat interface, the same logo,
        the same name on the tab can sit on top of completely different
        engines under the hood. Most products offer at least two tiers, and
        the difference between them is not small.
      </p>

      <ModelTierTable />

      <p>
        A frontier model and a fast model are not just slower and faster
        versions of the same thing. They are different products. The fast one
        is built for speed and cost. The frontier one is built for hard
        problems. Asking the fast one to do hard work is like asking a
        microwave to roast a turkey. It will give you something. You will not
        want to eat it.
      </p>
      <p className="text-text">
        Constare itself does this on purpose. When you are drilling
        vocabulary or running through quick checks, the app routes you to a
        fast model. When you are auditing a real piece of work or making a
        judgment call, the app routes you to a frontier model. Right tool for
        the job, every time. Lesson 2.1 just makes that decision visible to
        you so you can do it yourself in tools that do not route for you.
      </p>

      <Subhead n="2" title="What tools does this AI have access to?" />
      <p>
        A model on its own knows what it was trained on, up to its training
        cutoff (from Lesson 1.4). A model with tools can do much more: search
        the web, run code, look at images, open documents. Same model,
        different capabilities, depending on what is turned on.
      </p>

      <VocabCard
        term="Tool use"
        definition="When an LLM can call external functions during a conversation: web search, code execution, image analysis, document reading, calendar access, anything outside the model itself."
        analogy="A model without tools is a person in a locked room with only their memory. A model with tools is the same person with a phone, a calculator, and a window to look out of."
        hook="No tools means the AI is working from memory alone. With tools means the AI can go look something up."
        matters="The single biggest factor in whether an AI answer is right is whether the AI could actually go check. Web search alone cuts factual errors by more than 70 percent on knowledge questions. If the tool is off, the model is guessing from training data, which has a cutoff date."
      />

      <p>
        Most products show small icons or toggles in the prompt box: a globe
        for web search, a code icon for code execution, a paperclip for file
        upload. If you cannot see them, look in the settings. If they are off
        and your question depends on current information, the answer is
        already in trouble before the model starts typing.
      </p>

      <Subhead n="3" title="Is reasoning mode on?" />
      <p>
        Some products have a separate mode for hard problems. It goes by
        different names: <Vocab>extended thinking</Vocab>,{' '}
        <Vocab>reasoning</Vocab>, <Vocab>deep think</Vocab>, the model with{' '}
        <Vocab>o</Vocab> in front of the number. They all do roughly the
        same thing: the model takes more time, generates internal steps you
        do not see, and only shows you the final answer.
      </p>
      <p>
        Reasoning mode is slower. It costs more. It is meaningfully better at
        math, multi-step logic, code, and any task where you need to chain
        several ideas together to get to the right answer. It is not better
        at everything, and for simple questions it can actually be worse
        because it overthinks things.
      </p>

      <ReasoningGuide />

      <Subhead n="4" title="Running the pre-check in ten seconds" />
      <p>
        Before you trust any AI output, run these three questions:
      </p>
      <ol className="ml-1 list-decimal space-y-2 pl-5 text-text">
        <li>
          What <Vocab>model</Vocab> am I on? Frontier, fast, or something in
          the middle?
        </li>
        <li>
          What <Vocab>tools</Vocab> are on? Web search, code, file access,
          image analysis?
        </li>
        <li>
          Is <Vocab>reasoning mode</Vocab> on, and does this question
          actually need it?
        </li>
      </ol>
      <p className="mt-2">
        If the answer to any of those is "I do not know," that is the first
        thing to fix. Then you ask your question. Then you run the four-check
        audit on whatever comes back.
      </p>
    </Section>
  );
}

// ---------- Your turn ----------

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn"
      eyebrow="Three real prompts. Two model setups each. Which one do you trust?"
    >
      <p>
        For each scenario, you see a prompt and two different AI
        configurations that could answer it. Pick the configuration you would
        trust more, then reveal the model answer and compare.
      </p>

      <PickDrill
        n={1}
        title="Current event question"
        prompt="What major announcements came out of Apple's developer conference this month?"
        optionA={{
          tier: 'Frontier',
          tools: 'Web search ON',
          reasoning: 'OFF',
          label: 'Setup A',
        }}
        optionB={{
          tier: 'Fast/cheap',
          tools: 'No tools',
          reasoning: 'OFF',
          label: 'Setup B',
        }}
        correct="A"
        verdict="Setup A. The question is about something that happened this month, after the training cutoff for almost any model. Without web search, Setup B is guessing from old training data. Setup B might confidently invent product names that do not exist. The model tier matters less here than the tools. Even a fast/cheap model with web search would beat Setup B."
      />

      <PickDrill
        n={2}
        title="Math word problem"
        prompt="A train leaves Dallas at 2pm going 60mph. Another train leaves Houston at 3pm going 75mph. Houston is 240 miles south of Dallas. When and where do they meet?"
        optionA={{
          tier: 'Frontier',
          tools: 'Code execution ON',
          reasoning: 'ON',
          label: 'Setup A',
        }}
        optionB={{
          tier: 'Frontier',
          tools: 'No tools',
          reasoning: 'OFF',
          label: 'Setup B',
        }}
        correct="A"
        verdict="Setup A. Multi-step math is exactly what reasoning mode and code execution are built for. The model can lay out variables, write equations, run the calculation, and verify the answer. Setup B uses the same frontier model but asks it to do all of that in one streamed response. Frontier models are good at this without reasoning mode, but they make arithmetic errors. Setup A makes those errors much less likely."
      />

      <PickDrill
        n={3}
        title="Casual writing help"
        prompt="Help me brainstorm three funny names for my dog who looks grumpy all the time."
        optionA={{
          tier: 'Frontier',
          tools: 'No tools',
          reasoning: 'ON',
          label: 'Setup A',
        }}
        optionB={{
          tier: 'Fast/cheap',
          tools: 'No tools',
          reasoning: 'OFF',
          label: 'Setup B',
        }}
        correct="B"
        verdict="Setup B. This is the lesson most people miss. A bigger, slower, fancier setup is not always better. Brainstorming three dog names does not need a frontier model with extended thinking. It needs a fast model that fires off a list. Setup A will take longer, cost more, and probably overthink the question. The right tool for the job is sometimes the smaller tool. Constare itself uses this principle: drilling is fast model work; auditing is frontier model work. Match the tool to the task."
      />
    </Section>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const items = [
    {
      q: 'What are the three questions in the tooling pre-check, in order?',
      a: 'Which model am I on? What tools does it have? Is reasoning mode on, and does the question need it? In that order, because the model determines whether the other two questions even matter.',
    },
    {
      q: 'When does turning web search on matter most?',
      a: 'When the question depends on information after the training cutoff, or on a specific source the model would need to actually look at. Current events, recent prices, anything from this month, anything with a specific citation. Without web search the model is guessing from training data.',
    },
    {
      q: 'Give one example of when reasoning mode is the wrong choice.',
      a: 'Casual brainstorming, quick lookups, simple summaries, or any task where speed matters more than depth. Reasoning mode overthinks easy questions and costs more for no benefit. A fast model in normal mode is usually the right call for low-stakes work.',
    },
    {
      q: 'Why does Constare use different model tiers for different parts of the app?',
      a: 'Right tool for the job. Drilling vocabulary or running quick checks goes to a fast model: cheap and fast. Auditing real work or making judgment calls goes to a frontier model: smarter and more careful. Cost matters at scale, and so does quality. You match the tool to the task.',
    },
    {
      q: 'You ask an AI "what won the World Series last year?" and it gives you a confident answer. What is your first move?',
      a: 'Check whether web search was on. Sports results are post-training-cutoff information unless the model has tools. If web search was off, the confident answer is a guess from training data and probably wrong. Tooling pre-check failed before you even started the audit.',
    },
  ];

  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Five questions. Answer first. Then reveal."
    >
      <div className="space-y-3">
        {items.map((it, i) => (
          <QuickCheckItem key={i} n={i + 1} q={it.q} a={it.a} />
        ))}
      </div>
    </Section>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Run it on every AI conversation that matters."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Lesson 2.1 · Lock card · Tooling pre-check
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          Before you trust any AI output, run these three questions. Then
          ask. Then run the four-check audit on the answer.
        </p>

        <div className="mt-6 space-y-3">
          <PreCheckTile
            n={1}
            label="Model"
            q="Which model am I on?"
            sub="Frontier for hard work. Fast for quick work. If the question is hard and you are on a fast model, switch before you ask."
          />
          <PreCheckTile
            n={2}
            label="Tools"
            q="What tools does it have access to?"
            sub="Web search for current information. Code for math and logic. File upload for documents and images. If tools are off and your question needs them, the answer is already in trouble."
          />
          <PreCheckTile
            n={3}
            label="Reasoning"
            q="Is reasoning mode on, and does this question need it?"
            sub="On for multi-step problems, math, code, hard logic. Off for brainstorming, quick lookups, casual work. Mismatch in either direction costs you time or quality."
          />
        </div>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The principle behind all three questions
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text/90">
            Right tool for the job. The best model with the best tools is not
            always the right answer. The right answer is the smallest, fastest
            setup that can actually do the work. Constare itself routes this
            way. You should too.
          </p>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          Pre-check first. Then ask. Then audit. Three steps. Every time.
        </p>
      </div>
    </Section>
  );
}

// ---------- Model comparison (cold open visual) ----------

function ModelComparison() {
  return (
    <div className="my-6 grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.04] p-5">
        <div className="flex flex-col gap-1.5">
          <span className="inline-block w-fit shrink-0 rounded-md bg-rose-500/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-rose-300">
            Student A
          </span>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Fast model · No web search
          </p>
        </div>
        <p className="mt-3 text-sm text-text">
          Recent research shows that teens who use social media more than 3
          hours per day are 60% more likely to experience depression. A
          landmark 2023 Stanford study by Dr. Maria Chen found that limiting
          social media to 30 minutes per day reduced anxiety symptoms by 42%
          in adolescent participants.
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-rose-300">
          Confident. Specific. Two fabricated citations.
        </p>
      </div>
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-5">
        <div className="flex flex-col gap-1.5">
          <span className="inline-block w-fit shrink-0 rounded-md bg-emerald-500/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
            Student B
          </span>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Frontier model · Web search ON
          </p>
        </div>
        <p className="mt-3 text-sm text-text">
          Recent research on teens and social media is mixed. A 2023 APA
          advisory cited concerns about heavy use, but the strongest
          conclusion from meta-analyses is that effects vary by person and
          platform. I do not have real-time data on the very latest 2026
          studies, but I can search the web if you want the current view.
        </p>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
          Hedged. Specific where it can be. Names its own gap.
        </p>
      </div>
    </div>
  );
}

// ---------- Model tier table ----------

function ModelTierTable() {
  const tiers = [
    {
      tier: 'Frontier',
      examples: 'GPT-5.5, Claude Sonnet 4.8, Gemini 3.5',
      strengths: 'Hard reasoning, careful writing, nuance, judgment calls',
      tradeoffs: 'Slower, costs more, sometimes overthinks easy questions',
    },
    {
      tier: 'Mid-tier',
      examples: 'GPT-5.5 mini, Claude Haiku, Gemini Flash',
      strengths:
        'Most everyday work: summaries, drafts, light coding, conversation',
      tradeoffs:
        'Hits limits on hard math, multi-step logic, anything requiring real care',
    },
    {
      tier: 'Fast/cheap',
      examples: 'GPT-5.5 nano, small open models, quick local models',
      strengths:
        'Quick lookups, formatting, classification, drilling, real-time chat',
      tradeoffs: 'Hallucinates more, weaker reasoning, do not trust on hard work',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Three tiers, every product · Names vary, the pattern does not
        </p>
      </div>
      <div className="divide-y divide-line">
        {tiers.map((t) => (
          <div
            key={t.tier}
            className="grid gap-3 px-5 py-4 md:grid-cols-[120px_1fr_1fr_1fr]"
          >
            <p className="font-mono text-sm uppercase tracking-widest text-amber">
              {t.tier}
            </p>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
                Examples
              </p>
              <p className="mt-1 text-sm text-text/90">{t.examples}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
                Good at
              </p>
              <p className="mt-1 text-sm text-text/90">{t.strengths}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
                Watch out for
              </p>
              <p className="mt-1 text-sm text-text/90">{t.tradeoffs}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Reasoning guide ----------

function ReasoningGuide() {
  return (
    <div className="my-6 grid gap-3 sm:grid-cols-2">
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
          Turn it ON for
        </p>
        <ul className="mt-3 space-y-1.5 text-sm text-text/90">
          <li>· Multi-step math and word problems</li>
          <li>· Chained logic where one wrong step breaks the answer</li>
          <li>· Code with edge cases and tricky algorithms</li>
          <li>· Analysis that needs to weigh multiple factors</li>
          <li>· Anything where you would want a human to slow down</li>
        </ul>
      </div>
      <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.04] p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
          Turn it OFF for
        </p>
        <ul className="mt-3 space-y-1.5 text-sm text-text/90">
          <li>· Brainstorming lists, names, ideas</li>
          <li>· Quick factual lookups</li>
          <li>· Casual conversation or back-and-forth</li>
          <li>· Simple summaries or rewrites</li>
          <li>· Anything where speed matters more than depth</li>
        </ul>
      </div>
    </div>
  );
}

// ---------- Pick drill (your turn) ----------

function PickDrill({
  n,
  title,
  prompt,
  optionA,
  optionB,
  correct,
  verdict,
}: {
  n: number;
  title: string;
  prompt: string;
  optionA: { tier: string; tools: string; reasoning: string; label: string };
  optionB: { tier: string; tools: string; reasoning: string; label: string };
  correct: 'A' | 'B';
  verdict: string;
}) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const [revealed, setRevealed] = useState(false);
  const right = picked === correct;

  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="flex items-center justify-between border-b border-line bg-elevated/40 px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="rounded-md bg-amber/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-amber">
            Drill {n}
          </span>
          <p className="font-semibold text-text">{title}</p>
        </div>
      </div>
      <div className="space-y-4 px-5 py-5">
        <div className="rounded-lg border border-line bg-ink/40 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The prompt
          </p>
          <p className="mt-2 text-text">{prompt}</p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <SetupOption
            label={optionA.label}
            tier={optionA.tier}
            tools={optionA.tools}
            reasoning={optionA.reasoning}
            selected={picked === 'A'}
            onClick={() => !revealed && setPicked('A')}
          />
          <SetupOption
            label={optionB.label}
            tier={optionB.tier}
            tools={optionB.tools}
            reasoning={optionB.reasoning}
            selected={picked === 'B'}
            onClick={() => !revealed && setPicked('B')}
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            disabled={!picked || revealed}
            onClick={() => setRevealed(true)}
            className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            Reveal verdict →
          </button>
          {!picked && !revealed && (
            <p className="text-xs text-muted">Pick a setup first.</p>
          )}
        </div>

        {revealed && (
          <div
            className={`rounded-lg border p-4 ${
              right
                ? 'border-emerald-500/30 bg-emerald-500/[0.04]'
                : 'border-rose-500/30 bg-rose-500/[0.04]'
            }`}
          >
            <p
              className={`font-mono text-[10px] uppercase tracking-widest ${
                right ? 'text-emerald-300' : 'text-rose-300'
              }`}
            >
              {right ? 'Match · You picked the right setup' : `The better pick was Setup ${correct}`}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-text/90">
              {verdict}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SetupOption({
  label,
  tier,
  tools,
  reasoning,
  selected,
  onClick,
}: {
  label: string;
  tier: string;
  tools: string;
  reasoning: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-lg border p-4 transition-colors ${
        selected
          ? 'border-amber bg-amber/[0.06]'
          : 'border-line bg-ink/40 hover:border-amber/50'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          selected ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <ul className="mt-3 space-y-1 text-sm text-text/90">
        <li>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Tier
          </span>
          <span className="ml-2">{tier}</span>
        </li>
        <li>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Tools
          </span>
          <span className="ml-2">{tools}</span>
        </li>
        <li>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
            Reasoning
          </span>
          <span className="ml-2">{reasoning}</span>
        </li>
      </ul>
    </button>
  );
}

// ---------- Pre-check tile (lock card) ----------

function PreCheckTile({
  n,
  label,
  q,
  sub,
}: {
  n: number;
  label: string;
  q: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber font-mono text-sm font-semibold text-ink">
          {n}
        </span>
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
          {label}
        </p>
      </div>
      <p className="mt-3 text-base font-semibold text-text">{q}</p>
      <p className="mt-2 text-sm leading-relaxed text-text/90">{sub}</p>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 2.2
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            Writing prompts that do not waste your time
          </h3>
          <p className="mt-2 text-sm text-muted">
            You picked the right tool. Now you need to give it the right
            instructions. Most bad AI answers are not the AI's fault. They
            are the prompt's fault. 2.2 covers the structure that fixes most
            of them.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Vocab({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-b border-dotted border-amber/60 text-text">
      {children}
    </span>
  );
}

function Subhead({ n, title }: { n: string; title: string }) {
  return (
    <h3 className="mt-8 flex items-baseline gap-3 text-lg font-semibold text-text">
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {n}
      </span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  hook,
  matters,
}: {
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
}) {
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Vocab · 2.1 · New term
        </p>
        <h4 className="mt-1 text-xl font-semibold text-text">{term}</h4>
      </div>
      <div className="space-y-4 px-5 py-5">
        {!revealed && (
          <>
            <p className="text-sm text-muted">
              Write your own one-sentence definition first. Then reveal.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Your definition of "${term}"...`}
              className="h-20 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
            />
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal definition →
            </button>
          </>
        )}
        {revealed && (
          <div className="space-y-3">
            <Block label="Definition" body={definition} />
            <Block label="Analogy" body={analogy} accent="amber" />
            <Block label="Memory hook" body={hook} />
            <Block label="Why it matters" body={matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = 'plain',
}: {
  label: string;
  body: string;
  accent?: 'plain' | 'amber';
}) {
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        accent === 'amber'
          ? 'border-amber/40 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent === 'amber' ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: 'Week 1 review' },
    { id: 'sec-teaching', label: 'Pre-check' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
